import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, MessageCircle, Clock, CheckCircle, Paperclip, Smile, X, Loader2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import { smartCompressImage } from '../lib/imageCompression';

interface ConversationItem {
  phone: string;
  lastMessageAt: number;
  messageCount: number;
}

interface Message {
  id: number;
  phone: string;
  message: string;
  sender: 'user' | 'bot' | 'system';
  mediaUrl?: string;
  mediaType?: string;
  mediaCaption?: string;
  status?: string;
  createdAt: number;
}

// 精選 emoji（客服向：情緒/確認/物流/餐飲）
const EMOJIS = [
  '😀','😁','😂','🤣','😊','😍','🥰','😘','😎','🤔','😅','🙃','😴','😭','😡','🥳',
  '🤩','🙏','👍','👌','✌️','🤝','💪','🥬','🥕','🔥','❤️','💛','💚','✨','🎉','🎁',
  '📦','✅','❌','💰','🛒','📷','📅','⏰','🚚','📍','🙇','😳','😋','🤤','🍲','🏠',
];

const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp';

/**
 * WhatsApp 鐵律：經 WhatsApp 發送嘅圖必須 JPEG——WebP 會被當 sticker 靜默消失，
 * 大圖會被壓到模糊。非 JPEG 一律 canvas 重編碼（最長邊 2048、q0.85）；
 * JPEG 走智慧階梯壓縮（smartCompressImage 輸出同為 JPEG）。
 */
async function toWhatsAppJpeg(file: File): Promise<File> {
  if (file.type === 'image/jpeg') {
    const result = await smartCompressImage(file);
    return result.file;
  }
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) throw new Error('圖片轉換失敗');
  const base = file.name.replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

const AdminConversations: React.FC = () => {
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [selectedPhone, setSelectedPhone] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendLoading, setSendLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { can } = usePermissions();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchConversations();
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/public/admin/whatsapp/conversations');
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    } catch (e) {
      console.error('Failed to fetch conversations:', e);
    } finally {
      setLoading(false);
    }
  };

  const openConversation = async (phone: string) => {
    try {
      const res = await apiFetch(`/api/public/admin/whatsapp/conversations/${phone}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedPhone(data.phone);
        setMessages(data.messages || []);
        setShowModal(true);
      }
    } catch (e) {
      console.error('Failed to open conversation:', e);
    }
  };

  const isWindowOpen = () => {
    if (!messages.length) return false;
    const lastUserMessage = [...messages]
      .filter(m => m.sender === 'user')
      .sort((a, b) => b.createdAt - a.createdAt)[0];
    if (!lastUserMessage) return false;
    const twentyFourHoursAgo = Math.floor(Date.now() / 1000) - 24 * 60 * 60;
    return lastUserMessage.createdAt >= twentyFourHoursAgo;
  };

  const clearFile = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSendError(null);
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setSendError('只支援 JPG / PNG / WebP 圖片（發送時自動轉為 JPEG）');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setSendError('圖片太大（上限 10MB）');
      return;
    }
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSendMessage = async () => {
    if (!selectedPhone || sendLoading) return;
    const text = replyText.trim();
    if (!text && !selectedFile) return;

    setSendLoading(true);
    setSendError(null);
    try {
      let imageUrl: string | undefined;
      if (selectedFile) {
        // WhatsApp 鐵律：必須 JPEG（WebP 被當 sticker 靜默消失）
        const jpegFile = await toWhatsAppJpeg(selectedFile);
        const fd = new FormData();
        fd.append('file', jpegFile);
        fd.append('folder', 'whatsapp');
        const upRes = await apiFetch('/api/public/admin/media/upload', { method: 'POST', body: fd });
        const upJson = await upRes.json().catch(() => ({}));
        if (!upRes.ok || !upJson.url) {
          throw new Error(upJson.error || '圖片上載失敗');
        }
        imageUrl = upJson.url;
      }

      const res = await apiFetch('/api/public/admin/whatsapp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: selectedPhone, message: text, ...(imageUrl ? { imageUrl } : {}) })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || '發送失敗，請稍後再試');
      }
      setReplyText('');
      clearFile();
      setShowEmoji(false);
      await openConversation(selectedPhone);
    } catch (e) {
      setSendError(e instanceof Error ? e.message : '發送失敗：無法連線到伺服器');
    } finally {
      setSendLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const filteredConversations = conversations
    .filter(conv => conv.phone.includes(searchTerm))
    .sort((a, b) => b.lastMessageAt - a.lastMessageAt);

  return (
    <AdminLayout currentPage="conversations" onLogout={handleLogout}>
      <div className="mb-6"></div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="搜尋客戶電話號碼..."
          className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-orange-500 outline-none dark:bg-gray-800 dark:text-white"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Conversations List */}
      {loading ? (
        <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">
          加載中...
        </div>
      ) : filteredConversations.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">
          {conversations.length === 0 ? '沒有對話記錄' : '沒有符合條件的對話'}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredConversations.map(conv => (
            <div
              key={conv.phone}
              onClick={() => openConversation(conv.phone)}
              className="bg-white dark:bg-gray-800 rounded shadow p-4 cursor-pointer hover:shadow-lg hover:bg-orange-50 dark:hover:bg-orange-900/30 transition-all border-l-4 border-orange-500"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-semibold text-lg">📱 {conv.phone}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    最後消息：{conv.lastMessageAt ? new Date(conv.lastMessageAt * 1000).toLocaleString('zh-HK') : 'N/A'}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    消息數量：{conv.messageCount || 0} 條
                  </div>
                </div>
                <div className="text-orange-600 font-semibold whitespace-nowrap ml-4">查看對話 →</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Conversation Modal */}
      {showModal && selectedPhone && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-orange-500 to-orange-600 p-4 flex justify-between items-center border-b text-white rounded-t-lg">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <div>
                  <h2 className="text-xl font-bold">對話詳情</h2>
                  <p className="text-sm text-orange-100">📱 {selectedPhone}</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-2xl hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
              {messages.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">沒有消息</p>
              ) : (
                [...messages].reverse().map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'user' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.sender === 'user'
                          ? 'bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-white rounded-bl-none'
                          : 'bg-orange-600 text-white rounded-br-none'
                      }`}
                    >
                      {msg.mediaUrl && msg.mediaType?.startsWith('image/') && (
                        <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" className="block mb-2">
                          <img
                            src={msg.mediaUrl}
                            alt={msg.mediaCaption || 'WhatsApp 圖片'}
                            className="max-w-full max-h-64 rounded-lg object-contain border border-gray-200 dark:border-gray-600"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </a>
                      )}
                      {msg.mediaUrl && msg.mediaType && !msg.mediaType.startsWith('image/') && (
                        <a
                          href={msg.mediaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mb-2 text-sm underline break-all"
                        >
                          📎 附件（{msg.mediaType}）
                        </a>
                      )}
                      <div className="text-sm mb-1 break-words whitespace-pre-wrap">{msg.message}</div>
                      <div
                        className={`text-xs ${
                          msg.sender === 'user' ? 'text-gray-400 dark:text-gray-500' : 'text-orange-200'
                        }`}
                      >
                        {msg.status === 'failed' && <span className="mr-1 font-bold">⚠ 發送失敗</span>}
                        {msg.createdAt ? new Date(msg.createdAt * 1000).toLocaleString('zh-HK') : 'N/A'}
                        {msg.sender !== 'user' && <span className="ml-1 opacity-75">· {msg.sender === 'system' ? '系統' : '回覆'}</span>}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            {can(PERMISSIONS.CONVERSATIONS_SEND) ? (
              <div className="p-4 border-t dark:border-gray-700 bg-white dark:bg-gray-800 rounded-b-lg space-y-3">
                {/* 24-hour window status */}
                <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                  isWindowOpen()
                    ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                    : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
                }`}>
                  {isWindowOpen() ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                  {isWindowOpen()
                    ? '24 小時對話窗口開啟中 — 可直接回覆文字／圖片'
                    : '24 小時對話窗口已關閉 — 請等客人主動聯絡，系統會自動以 Template 跟進'}
                </div>

                {sendError && (
                  <div className="p-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
                    <span className="font-semibold">發送失敗：</span>{sendError}
                  </div>
                )}

                {/* 選中圖片預覽 */}
                {selectedFile && previewUrl && (
                  <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                    <img src={previewUrl} alt="預覽" className="w-12 h-12 object-cover rounded" />
                    <div className="flex-1 min-w-0 text-xs text-gray-600 dark:text-gray-300 truncate">
                      {selectedFile.name}（將以 JPEG 發送）
                    </div>
                    <button onClick={clearFile} className="p-1 text-gray-400 hover:text-red-600" title="移除圖片">
                      <X size={16} />
                    </button>
                  </div>
                )}

                {/* Emoji picker：浮出式，不佔常駐空間 */}
                {showEmoji && (
                  <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 grid grid-cols-12 gap-0.5 max-h-32 overflow-y-auto">
                    {EMOJIS.map((em, i) => (
                      <button
                        key={`${em}-${i}`}
                        onClick={() => setReplyText((prev) => prev + em)}
                        className="text-xl leading-7 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                )}

                <div className="flex gap-2">
                  <textarea
                    value={replyText}
                    onChange={e => {
                      setReplyText(e.target.value);
                      setSendError(null);
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 110) + 'px';
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder={isWindowOpen() ? "輸入回覆訊息... (按 Enter 發送)" : "窗口關閉，無法發送訊息"}
                    rows={2}
                    className="flex-1 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none resize-none dark:bg-gray-800 dark:text-white"
                    disabled={sendLoading || !isWindowOpen()}
                  />
                </div>

                {/* 工具列：📎 圖片｜😊 emoji｜(彈性空間)｜發送 */}
                <div className="flex items-center gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={sendLoading || !isWindowOpen()}
                    title="附加圖片"
                    className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${selectedFile ? 'text-orange-600 bg-orange-50 dark:bg-orange-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20'}`}
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setShowEmoji(v => !v)}
                    disabled={sendLoading || !isWindowOpen()}
                    title="Emoji"
                    className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${showEmoji ? 'text-orange-600 bg-orange-50 dark:bg-orange-900/20' : 'text-gray-500 dark:text-gray-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20'}`}
                  >
                    <Smile className="w-5 h-5" />
                  </button>
                  <span className="flex-1" />
                  <button
                    onClick={handleSendMessage}
                    disabled={sendLoading || (!replyText.trim() && !selectedFile) || !isWindowOpen()}
                    className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white p-2 rounded-lg transition-colors"
                    title="發送"
                  >
                    {sendLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-lg text-center">
                <p className="text-sm text-gray-400">無權限發送消息</p>
              </div>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminConversations;
